<template>
  <div class="min-h-screen bg-gray-900">
    <!-- 导航栏 -->
    <nav class="nav-container">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center h-16">
          <button @click="goBack" class="mr-4 text-gray-400 hover:text-white transition-colors flex-shrink-0">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
          <div class="flex items-center space-x-2">
            <svg class="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <h1 class="text-xl font-bold text-white">每日最新资源</h1>
          </div>
        </div>
      </div>
    </nav>

    <!-- 主要内容 -->
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8">
      <!-- Tab切换 -->
      <div class="flex space-x-2 mb-6 overflow-x-auto pb-2">
        <button
          @click="activeTab = 'movie'"
          class="tab-button flex-shrink-0"
          :class="{ 'tab-active': activeTab === 'movie' }"
        >
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"/>
          </svg>
          电影
          <span v-if="movieCount > 0" class="ml-2 px-2 py-1 bg-blue-600 rounded-full text-xs">
            {{ movieCount }}
          </span>
        </button>
        <button
          @click="activeTab = 'tv'"
          class="tab-button flex-shrink-0"
          :class="{ 'tab-active': activeTab === 'tv' }"
        >
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
          </svg>
          电视剧/综艺
          <span v-if="tvCount > 0" class="ml-2 px-2 py-1 bg-blue-600 rounded-full text-xs">
            {{ tvCount }}
          </span>
        </button>
        <button
          @click="activeTab = 'daily'"
          class="tab-button flex-shrink-0"
          :class="{ 'tab-active': activeTab === 'daily' }"
        >
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
          </svg>
          资源杂烩
          <span v-if="dailyCount > 0" class="ml-2 px-2 py-1 bg-blue-600 rounded-full text-xs">
            {{ dailyCount }}
          </span>
        </button>
      </div>

      <!-- 搜索框 -->
      <div class="mb-6">
        <div class="flex gap-3">
          <div class="flex-1 relative">
            <svg class="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="activeTab === 'movie' ? '搜索电影名称...' : (activeTab === 'tv' ? '搜索电视剧/综艺名称...' : '搜索教程/音乐/资源...')"
              @keyup.enter="handleSearch"
              class="search-input w-full search-input-with-icon"
            />
          </div>
          <button
            @click="handleSearch"
            class="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors duration-200 flex items-center"
          >
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            搜索
          </button>
          <button
            v-if="searchQuery"
            @click="clearSearch"
            class="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
          >
            清除
          </button>
        </div>
      </div>

      <!-- 更新时间和统计 -->
      <div v-if="!loading && updateTime" class="mb-4 flex items-center justify-between">
        <div class="flex items-center text-gray-400 text-sm">
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <span>更新时间：{{ updateTime }}</span>
        </div>
        <div class="text-gray-400 text-sm">
          共 {{ currentResources.length }} 个资源
        </div>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p class="text-gray-400 mt-4">加载中...</p>
      </div>

      <!-- 错误信息 -->
      <div v-else-if="error" class="text-center py-12">
        <svg class="w-16 h-16 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <p class="text-red-500 mt-4">{{ error }}</p>
        <button
          @click="loadResources"
          class="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors duration-200"
        >
          重试
        </button>
      </div>

      <!-- 资源列表 -->
      <div v-else-if="currentResources.length > 0" class="space-y-4">
        <div
          v-for="(resource, index) in currentResources"
          :key="index"
          class="resource-card bg-gray-800/80 backdrop-blur rounded-xl border border-gray-700/50 overflow-hidden hover:border-gray-600 transition-all duration-200"
        >
          <!-- 卡片头部：标题 -->
          <div class="p-4 pb-3">
            <div class="flex items-start gap-3">
              <!-- 序号 -->
              <span 
                class="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-lg text-sm font-bold"
                :class="{
                  'bg-gradient-to-br from-red-500 to-orange-500 text-white': index < 3,
                  'bg-gray-700 text-gray-400': index >= 3
                }"
              >
                {{ index + 1 }}
              </span>
              <!-- 标题和类型 -->
              <div class="flex-1 min-w-0">
                <h3 class="text-white font-medium text-base leading-relaxed break-words">
                  {{ resource.title }}
                </h3>
                <span class="inline-block mt-1.5 px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">
                  {{ resource.type }}
                </span>
              </div>
            </div>
          </div>

          <!-- 卡片底部：网盘按钮 -->
          <div class="px-4 pb-4 flex gap-2">
            <!-- 百度网盘 - 蓝色 -->
            <button
              v-if="resource.baiduLink"
              @click="handleExtractLink(resource.baiduLink, '百度网盘')"
              :disabled="extractingUrls.has(resource.baiduLink)"
              class="flex-1 bg-gradient-to-r from-[#06A7FF] to-[#0095E8] hover:from-[#0095E8] hover:to-[#0085D0] text-white px-4 py-2.5 rounded-lg transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium shadow-lg shadow-[#06A7FF]/20"
            >
              <span v-if="extractingUrls.has(resource.baiduLink)" class="flex items-center">
                <div class="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                提取中...
              </span>
              <span v-else class="flex items-center">
                <img src="/favicon-baidu.ico" alt="百度" class="w-4 h-4 mr-1.5" />
                百度网盘
                <span v-if="resource.extractCode" class="ml-1.5 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                  {{ resource.extractCode }}
                </span>
              </span>
            </button>

            <!-- 夸克网盘 - 紫色 -->
            <button
              v-if="resource.quarkLink"
              @click="handleExtractLink(resource.quarkLink, '夸克网盘')"
              :disabled="extractingUrls.has(resource.quarkLink)"
              class="flex-1 bg-gradient-to-r from-[#8B5CF6] to-[#7C3AED] hover:from-[#7C3AED] hover:to-[#6D28D9] text-white px-4 py-2.5 rounded-lg transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium shadow-lg shadow-[#8B5CF6]/20"
            >
              <span v-if="extractingUrls.has(resource.quarkLink)" class="flex items-center">
                <div class="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                提取中...
              </span>
              <span v-else class="flex items-center">
                <img src="/favicon-quark.ico" alt="夸克" class="w-4 h-4 mr-1.5" />
                夸克网盘
              </span>
            </button>

            <!-- 没有链接提示 -->
            <div v-if="!resource.baiduLink && !resource.quarkLink" class="flex-1 text-gray-500 text-sm py-2.5 text-center bg-gray-700/50 rounded-lg">
              暂无可用链接
            </div>
          </div>
        </div>
      </div>

      <!-- 空状态或资源较少时的引导 -->
      <div v-if="!loading && (currentResources.length === 0 || (searchQuery && currentResources.length < 5))" class="mt-6">
        <!-- 引导卡片 -->
        <div class="bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-xl p-5">
          <div class="flex items-start space-x-4">
            <div class="flex-shrink-0">
              <div class="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
              </div>
            </div>
            <div class="flex-1">
              <h3 class="text-white font-medium mb-1">
                {{ currentResources.length === 0 ? '每日更新暂无此资源' : '每日更新资源较少' }}
              </h3>
              <p class="text-gray-400 text-sm mb-3">
                {{ searchQuery ? `"${searchQuery}" 在每日更新中` : '当前分类' }}{{ currentResources.length === 0 ? '没有找到结果' : `仅有 ${currentResources.length} 个结果` }}，试试全网搜索获取更多资源
              </p>
              <button
                @click="goToGlobalSearch"
                class="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200"
              >
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
                全网搜索 "{{ searchQuery || '更多资源' }}"
                <svg class="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 完全空状态（无搜索词时） -->
      <div v-if="!loading && currentResources.length === 0 && !searchQuery" class="text-center py-16">
        <svg class="w-16 h-16 text-gray-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <p class="text-gray-400 mt-4">暂无资源</p>
      </div>
    </main>

    <!-- 全局loading遮罩层 -->
    <div v-if="globalLoading" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-gray-800 rounded-lg p-8 max-w-sm w-full mx-4">
        <div class="flex flex-col items-center">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p class="text-white text-lg font-medium">正在提取链接...</p>
          <p class="text-gray-400 text-sm mt-2">请稍等，这可能需要一些时间</p>
        </div>
      </div>
    </div>

    <!-- 转存成功弹窗 - 移动端 -->
    <div v-if="showTransferModal && isMobile" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
      <div class="bg-gray-800 rounded-lg max-w-sm w-full max-h-[90vh] overflow-y-auto">
        <!-- 弹窗头部 -->
        <div class="flex justify-between items-center px-4 py-3 border-b border-gray-700">
          <h3 class="text-base font-bold text-white flex items-center">
            <svg class="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            提取成功
          </h3>
          <button @click="closeTransferModal" class="text-gray-400 hover:text-white transition-colors">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <!-- 弹窗内容 -->
        <div class="p-4">
          <!-- 友情提示 -->
          <div class="mb-3 bg-gradient-to-r from-yellow-600 to-orange-600 rounded-lg p-2.5 shadow-lg">
            <div class="flex items-start">
              <svg class="w-4 h-4 text-white mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
              <p class="text-white text-xs leading-relaxed">
                热门资源夸克易和谐，如果出现<span class="font-semibold underline">缺集、空文件</span>等情况都是被和谐了，请换一个资源即可。
              </p>
            </div>
          </div>

          <!-- 提示信息 -->
          <div class="bg-blue-600 text-white px-3 py-2 rounded-lg mb-3 flex items-center text-xs">
            <svg class="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>资源将在5分钟后失效，请及时保存到网盘!</span>
          </div>

          <!-- 分享链接 -->
          <div class="mb-3">
            <label class="block text-gray-400 text-xs mb-1">分享链接：</label>
            <div class="flex items-center space-x-2">
              <input type="text" :value="transferResult.share_url" readonly class="flex-1 bg-gray-700 text-white px-2 py-1.5 rounded border border-gray-600 text-xs focus:outline-none" />
              <button @click="copyShareUrl" class="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1.5 rounded transition-colors duration-200 flex items-center text-xs">
                <span v-if="copied" class="flex items-center">
                  <svg class="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                  已复制
                </span>
                <span v-else>复制</span>
              </button>
            </div>
          </div>

          <!-- 操作按钮 -->
          <div class="flex space-x-2">
            <button @click="openLink(transferResult.share_url)" class="flex-1 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded transition-colors duration-200 flex items-center justify-center text-sm">
              <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
              </svg>
              打开链接
            </button>
            <button @click="closeTransferModal" class="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded transition-colors duration-200 text-sm">关闭</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 转存成功弹窗 - PC端（强制扫码） -->
    <div v-if="showTransferModal && !isMobile" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-gray-800 rounded-xl max-w-md w-full shadow-2xl">
        <!-- 弹窗头部 -->
        <div class="flex justify-between items-center px-6 py-4 border-b border-gray-700">
          <h3 class="text-lg font-bold text-white flex items-center">
            <svg class="h-6 w-6 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            提取成功
          </h3>
          <button @click="closeTransferModal" class="text-gray-400 hover:text-white transition-colors">
            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <!-- 弹窗内容 -->
        <div class="p-6">
          <!-- 友情提示 -->
          <div class="mb-4 bg-gradient-to-r from-yellow-600 to-orange-600 rounded-lg p-3 shadow-lg">
            <div class="flex items-start">
              <svg class="w-5 h-5 text-white mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
              <p class="text-white text-sm leading-relaxed">
                热门资源夸克易和谐，如果出现<span class="font-semibold underline">缺集、空文件</span>等情况都是被和谐了，请换一个资源即可。
              </p>
            </div>
          </div>

          <!-- PC端扫码提示 -->
          <div class="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-3 rounded-lg mb-4 text-center">
            <div class="flex items-center justify-center mb-1">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"/>
              </svg>
              <span class="font-semibold">请使用手机扫码转存</span>
            </div>
            <p class="text-xs text-blue-100">资源将在5分钟后失效，请及时保存到网盘!</p>
          </div>
          
          <!-- 大二维码 -->
          <div class="mb-4">
            <div class="bg-white p-4 rounded-xl flex justify-center shadow-inner">
              <div class="text-center">
                <div v-if="qrCodeUrl" class="w-48 h-48 flex items-center justify-center rounded-lg mx-auto">
                  <img :src="qrCodeUrl" alt="分享链接二维码" class="w-full h-full object-contain" />
                </div>
                <div v-else class="w-48 h-48 flex items-center justify-center rounded-lg mx-auto bg-gray-100">
                  <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
                <p class="text-gray-600 text-sm mt-3 font-medium">打开夸克/百度APP或微信/浏览器扫码转存</p>
              </div>
            </div>
          </div>

          <!-- 百度网盘提示 -->
          <div class="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div class="flex items-start">
              <svg class="w-4 h-4 text-blue-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <p class="text-blue-700 text-xs leading-relaxed">
                <span class="font-semibold">百度网盘用户注意：</span>建议使用<span class="font-semibold text-blue-800">微信扫码</span>打开链接，可自动填入提取码。直接用百度APP扫码可能无法自动填入提取码。
              </p>
            </div>
          </div>

          <!-- 关闭按钮 -->
          <button @click="closeTransferModal" class="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-3 rounded-lg transition-colors duration-200 text-sm font-medium">关闭</button>
        </div>
      </div>
    </div>

    <!-- 错误提示 -->
    <div v-if="transferError" class="fixed top-4 right-4 bg-red-600 text-white px-6 py-4 rounded-lg shadow-lg z-50 max-w-md">
      <div class="flex items-start space-x-3">
        <svg class="h-6 w-6 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <div class="flex-1">
          <p class="font-medium">提取失败</p>
          <p class="text-sm mt-1">{{ transferError }}</p>
        </div>
        <button
          @click="transferError = ''"
          class="text-white hover:text-gray-200 transition-colors flex-shrink-0"
        >
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import dailyResourcesService from '../services/dailyResources'
import axios from 'axios'
import QRCode from 'qrcode'
import { filterBlockedResources } from '../utils/resourceFilter'

export default {
  name: 'DailyUpdate',
  data() {
    return {
      activeTab: 'movie', // 当前激活的tab: 'movie'、'tv' 或 'daily'
      searchQuery: '', // 搜索关键词
      movieResources: [], // 电影资源列表
      tvResources: [], // 电视剧资源列表
      dailyResources: [], // 资源杂烩列表
      movieCount: 0,
      tvCount: 0,
      dailyCount: 0,
      updateTime: '', // 更新时间
      loading: false,
      error: '',
      extractingUrls: new Set(), // 正在提取的URL集合
      globalLoading: false,
      showTransferModal: false,
      transferResult: null,
      transferError: '',
      copied: false,
      qrCodeUrl: '',
      isMobile: false,
      isInitialized: false // 标记是否已初始化完成
    }
  },
  computed: {
    // 当前显示的资源列表
    currentResources() {
      if (this.activeTab === 'movie') return this.movieResources
      if (this.activeTab === 'tv') return this.tvResources
      return this.dailyResources
    }
  },
  watch: {
    // 监听tab切换，重新加载资源（仅在初始化完成后生效）
    activeTab() {
      if (this.isInitialized) {
        this.searchQuery = ''
        this.loadResources()
      }
    },
    transferResult(newVal) {
      if (newVal && newVal.share_url) {
        this.$nextTick(() => {
          this.generateQRCode(newVal.share_url)
        })
      }
    }
  },
  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.handleResize)
    
    // 从URL参数获取搜索关键词和类型
    const urlSearch = this.$route.query.search || ''
    const urlType = this.$route.query.type || ''
    
    // 如果URL有type参数，设置activeTab
    if (urlType === 'movie' || urlType === 'tv' || urlType === 'daily') {
      this.activeTab = urlType
    }
    
    // 如果URL有search参数，设置搜索关键词
    if (urlSearch) {
      this.searchQuery = urlSearch
    }
    
    // 加载资源
    this.loadResources()
    
    // 标记初始化完成
    this.$nextTick(() => {
      this.isInitialized = true
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    checkMobile() {
      // 使用视口宽度判断，768px以下为移动端
      this.isMobile = window.innerWidth < 768
    },
    handleResize() {
      this.checkMobile()
    },
    goBack() {
      this.$router.push('/')
    },
    
    // 加载资源
    async loadResources() {
      this.loading = true
      this.error = ''
      
      try {
        const type = this.activeTab // 'movie'、'tv' 或 'daily'
        const result = await dailyResourcesService.getDailyResources(type, this.searchQuery)
        
        if (result.success) {
          const data = result.data
          const filteredResources = filterBlockedResources(data.list || [])
          
          if (this.activeTab === 'movie') {
            this.movieResources = filteredResources
            this.movieCount = filteredResources.length
          } else if (this.activeTab === 'tv') {
            this.tvResources = filteredResources
            this.tvCount = filteredResources.length
          } else {
            this.dailyResources = filteredResources
            this.dailyCount = filteredResources.length
          }
          
          this.updateTime = data.updateTime || ''
        } else {
          this.error = result.message || '加载失败'
        }
      } catch (error) {
        console.error('加载资源失败:', error)
        this.error = '网络错误，请稍后重试'
      } finally {
        this.loading = false
      }
    },
    
    // 处理搜索
    handleSearch() {
      this.loadResources()
    },
    
    // 清除搜索
    clearSearch() {
      this.searchQuery = ''
      this.loadResources()
    },
    
    // 处理提取链接
    async handleExtractLink(encryptedUrl, diskType) {
      try {
        this.globalLoading = true
        this.extractingUrls.add(encryptedUrl)
        this.transferError = ''
        this.transferResult = null
        this.qrCodeUrl = ''
        
        const response = await axios.post('/api/transfer-share/transfer-share', {
          encrypted_url: encryptedUrl
        })
        
        if (response.data.code === 200) {
          this.transferResult = {
            share_url: response.data.data.share_url,
            original_url: response.data.data.original_url,
            passcode: response.data.data.passcode,
            file_info: response.data.data.file_info
          }
          this.showTransferModal = true
        } else {
          this.transferError = response.data.message || '链接提取失败'
        }
      } catch (error) {
        console.error('提取链接失败:', error)
        if (error.response && error.response.data) {
          this.transferError = error.response.data.message || '链接提取失败'
        } else if (error.request) {
          this.transferError = '网络错误，请检查网络连接后重试'
        } else {
          this.transferError = '链接提取失败，请稍后重试'
        }
      } finally {
        this.globalLoading = false
        this.extractingUrls.delete(encryptedUrl)
      }
    },
    
    // 复制分享链接
    async copyShareUrl() {
      if (!this.transferResult) return
      
      try {
        await navigator.clipboard.writeText(this.transferResult.share_url)
        this.copied = true
        setTimeout(() => {
          this.copied = false
        }, 2000)
      } catch (error) {
        console.error('复制失败:', error)
      }
    },
    
    // 关闭转存弹窗
    closeTransferModal() {
      this.showTransferModal = false
      this.transferResult = null
      this.transferError = ''
      this.copied = false
      this.qrCodeUrl = ''
    },
    
    // 打开链接
    openLink(url) {
      window.open(url, '_blank')
    },
    
    // 跳转到全网搜索页面
    goToGlobalSearch() {
      const keyword = this.searchQuery || ''
      if (keyword) {
        window.open(`/search?q=${encodeURIComponent(keyword.trim())}`, '_blank')
      } else {
        this.$router.push('/search')
      }
    },
    
    // 生成二维码
    async generateQRCode(url) {
      try {
        // PC端生成更大的二维码
        const size = this.isMobile ? 128 : 256
        this.qrCodeUrl = await QRCode.toDataURL(url, {
          width: size,
          height: size,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#ffffff'
          }
        })
      } catch (error) {
        console.error('生成二维码失败:', error)
      }
    }
  }
}
</script>

<style scoped>
.nav-container {
  background-color: #1a1a1a;
  border-bottom: 1px solid #2d2d2d;
  position: sticky;
  top: 0;
  z-index: 40;
  backdrop-filter: blur(8px);
  background-color: rgba(26, 26, 26, 0.95);
}

.search-input {
  background-color: #2d2d2d;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid #3d3d3d;
  transition: all 0.2s;
}

.search-input-with-icon {
  padding-left: 2.5rem !important;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.search-input::placeholder {
  color: #6b7280;
}

.tab-button {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  background-color: #2d2d2d;
  color: #9ca3af;
  border-radius: 0.5rem;
  transition: all 0.2s;
  font-weight: 500;
}

.tab-button:hover {
  background-color: #374151;
  color: #e5e7eb;
}

.tab-active {
  background-color: #3b82f6;
  color: white;
}

.resource-card {
  border: 1px solid #2d2d2d;
  transition: all 0.2s;
}

.resource-card:hover {
  border-color: #3b82f6;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
}

@media (max-width: 768px) {
  .tab-button {
    font-size: 0.875rem;
    padding: 0.625rem 1rem;
  }
  
  .resource-card {
    padding: 1rem;
  }
}
</style>
